import React, { useEffect, useMemo, useRef } from 'react';

import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import type { ChartPreviewRequest } from './chartPreviewUtils';
import { applyPreviewTooltipStyle, buildPieTooltipHTML, getPreviewColors } from './chartPreviewStyles';
import useAmChartsResize from './useAmChartsResize';

type Props = {
  chart: ChartPreviewRequest;
  height?: number | string;
  width?: number | string;
};

const PieChartPreview = ({ chart, height, width }: Props): React.JSX.Element => {
  const ref = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<am4charts.PieChart | null>(null);
  const theme = useTheme();
  useAmChartsResize(ref, chartRef);
  const colors = useMemo(() => getPreviewColors(theme), [theme]);
  const { categoryField, valueFields, data } = chart;
  const valueField = valueFields[0];
  const hasFixedHeight = typeof height !== 'undefined';
  const legendItems = useMemo<Array<{ label: string; value: number; color: string }>>(
    () =>
      data.map((row, index) => {
        const rawValue = row[valueField];
        const value = typeof rawValue === 'number' ? rawValue : 0;

        return {
          label: String(row[categoryField] ?? ''),
          value,
          color: colors[index % colors.length]?.hex ?? theme.palette.primary.main,
        };
      }),
    [categoryField, colors, data, theme.palette.primary.main, valueField],
  );

  const formatCurrency = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      }),
    [],
  );

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    if (!categoryField || !valueFields?.length || !data?.length) {
      return;
    }

    const chartInstance = am4core.create(container, am4charts.PieChart);
    chartRef.current = chartInstance;
    chartInstance.data = data;
    chartInstance.colors.list = colors;
    chartInstance.background.fill = am4core.color(theme.palette.background.paper);
    chartInstance.background.fillOpacity = 0;
    chartInstance.innerRadius = am4core.percent(35);
    chartInstance.responsive.enabled = true;
    chartInstance.numberFormatter.numberFormat = '$#,###.00';

    const pieSeries = chartInstance.series.push(new am4charts.PieSeries());
    pieSeries.dataFields.value = valueFields[0];
    pieSeries.dataFields.category = categoryField;
    pieSeries.colors.list = colors;
    pieSeries.slices.template.stroke = am4core.color(theme.palette.background.paper);
    pieSeries.slices.template.strokeWidth = 2;
    pieSeries.slices.template.strokeOpacity = 1;
    pieSeries.slices.template.cornerRadius = 4;
    pieSeries.slices.template.innerCornerRadius = 4;
    pieSeries.labels.template.disabled = true;
    pieSeries.ticks.template.disabled = true;
    pieSeries.slices.template.tooltipHTML = buildPieTooltipHTML(theme);

    if (pieSeries.tooltip) {
      applyPreviewTooltipStyle(pieSeries.tooltip, theme, {
        background: 'paper',
        opacity: 0.8,
        strokeWidth: 0,
        textColor: theme.palette.text.secondary,
      });
    }

    return () => {
      chartInstance.dispose();
      chartRef.current = null;
    };
  }, [chart, colors, theme, data, valueFields, categoryField]);

  return (
    <Box
      sx={{
        width: width ?? '100%',
        height: hasFixedHeight ? height : 'auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        ref={ref}
        sx={{
          width: '100%',
          height: hasFixedHeight ? 'auto' : 240,
          flex: hasFixedHeight ? '1 1 auto' : '0 0 auto',
          minHeight: hasFixedHeight ? 120 : undefined,
        }}
      />
      {legendItems.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            paddingTop: theme.spacing(1),
            maxHeight: hasFixedHeight ? '40%' : 'none',
            overflowY: hasFixedHeight ? 'auto' : 'visible',
          }}
        >
          {legendItems.map((item, index) => (
            <Box
              key={`${item.label}-${index}`}
              sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.75), minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Box
                  component='div'
                  sx={{
                    ...theme.typography.captionRegular,
                    color: theme.palette.text.primary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 140,
                  }}
                >
                  {item.label}
                </Box>
                <Box component='div' sx={{ ...theme.typography.captionRegular, color: theme.palette.text.secondary }}>
                  {formatCurrency.format(item.value)}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PieChartPreview;
